import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { X, Send, Paperclip, CheckCircle, Loader2 } from "lucide-react";
import { chunkedUpload } from "@/utils/chunkedUpload";

const BOT_PHOTO = "/assets/rui_pires_bot.png";

function getOrCreateSession() {
  // Stores a non-sensitive random session ID for AI chat continuity (not auth credentials)
  const KEY = "acd_ai_session";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

const SUGGESTIONS = [
  "Arquiva este documento no processo do [cliente]",
  "Lança 300€ de honorários no processo do [cliente]",
  "Qual o estado do processo do [cliente]?",
  "Cria tarefa para rever o processo do [cliente]",
  "Adiciona prazo de contestação até [data] no processo do [cliente]",
];

// ── Easter Egg Overlay ────────────────────────────────────────────────────────
function EasterEggOverlay({ onClose }) {
  const [phase, setPhase] = useState(0); // 0=logo, 1=photo, 2=text

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 600);
    const t2 = setTimeout(() => setPhase(2), 1400);
    const t3 = setTimeout(onClose, 7000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onClose]);

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center overflow-hidden rounded-2xl"
      style={{ background: "linear-gradient(160deg, #05304a 0%, #0b679e 50%, #084e78 100%)" }}
    >
      {/* Particles */}
      {["15%,10%","80%,15%","5%,70%","90%,60%","50%,85%","25%,40%","70%,80%"].map((pos, i) => (
        <div
          key={pos}
          className="absolute w-1 h-1 bg-white rounded-full opacity-60"
          style={{
            left: pos.split(",")[0], top: pos.split(",")[1],
            animation: `ping ${1 + i * 0.3}s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`
          }}
        />
      ))}

      {/* Logo ACD */}
      <div
        className="mb-5 transition-all duration-700"
        style={{
          opacity: phase >= 0 ? 1 : 0,
          transform: phase >= 0 ? "scale(1) translateY(0)" : "scale(0.5) translateY(-20px)"
        }}
      >
        <img
          src="/assets/logo.png"
          alt="ACD"
          className="h-14 object-contain"
          style={{ filter: "brightness(0) invert(1)" }}
          onError={e => { e.target.style.display="none"; }}
        />
      </div>

      {/* Rui Pires photo */}
      <div
        className="relative mb-4 transition-all duration-500"
        style={{
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? "scale(1)" : "scale(0.3)"
        }}
      >
        <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white/40 shadow-2xl">
          <img src={BOT_PHOTO} alt="Rui Pires" className="w-full h-full object-cover" />
        </div>
        <div
          className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center text-xs"
          style={{ opacity: phase >= 2 ? 1 : 0, transition: "opacity 0.3s 0.5s" }}
        >
          ✓
        </div>
      </div>

      {/* Text */}
      <div
        className="text-center transition-all duration-500 px-4"
        style={{ opacity: phase >= 2 ? 1 : 0, transform: phase >= 2 ? "translateY(0)" : "translateY(10px)" }}
      >
        <p className="text-white font-bold text-xl mb-1">Rui Pires BOT</p>
        <p className="text-blue-200 text-xs mb-1">Assistente Jurídico Premium™</p>
        <p className="text-white/40 text-[10px]">Desde 2026 na vanguarda do direito digital 😎</p>
        <p className="text-white/30 text-[9px] mt-1">Powered by Gemini AI</p>
      </div>

      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>

      <p className="absolute bottom-3 text-white/25 text-[9px]">Fecha automaticamente...</p>
    </div>
  );
}

// ── BotAvatar ─────────────────────────────────────────────────────────────────
function BotAvatar({ size = "sm" }) {
  const cls = size === "sm"
    ? "w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-0.5 border border-white/20 shadow"
    : "w-9 h-9 rounded-full overflow-hidden border-2 border-white/30 shadow";
  return (
    <div className={cls}>
      <img src={BOT_PHOTO} alt="Rui Pires BOT" className="w-full h-full object-cover" />
    </div>
  );
}

// ── MessageBubble ──────────────────────────────────────────────────────────────
function MessageBubble({ msg, onExecute, onEasterEgg }) {
  const [selectedId, setSelectedId] = useState(
    msg.processo_choices?.length === 1 ? msg.processo_choices[0].id : null
  );
  const [dateOverride, setDateOverride] = useState(msg.params?.data || "");
  const [eggDismissed, setEggDismissed] = useState(false);

  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-[#0b679e] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%] shadow-sm">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
          <p className="text-[10px] text-blue-200 mt-1 text-right">{msg.ts}</p>
        </div>
      </div>
    );
  }

  if (msg.role === "system") {
    return (
      <div className="flex justify-center">
        <p className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">{msg.content}</p>
      </div>
    );
  }

  return (
    <div className="flex justify-start gap-2">
      <BotAvatar size="sm" />
      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] shadow-sm">
        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{msg.content}</p>

        {/* Easter egg prompt */}
        {msg.is_easter_egg_prompt && !eggDismissed && (
          <div className="mt-3 border-t border-slate-100 pt-3 flex gap-2">
            <button
              onClick={() => { setEggDismissed(true); onEasterEgg(); }}
              data-testid="ai-easter-egg-yes-btn"
              className="flex-1 bg-[#0b679e] hover:bg-[#084e78] text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
            >
              Sim, quero ver! 🎬
            </button>
            <button
              onClick={() => setEggDismissed(true)}
              data-testid="ai-easter-egg-no-btn"
              className="flex-1 border border-slate-200 text-slate-500 hover:bg-slate-50 text-xs px-3 py-1.5 rounded-lg transition-colors"
            >
              Não, obrigado
            </button>
          </div>
        )}

        {/* Ação pendente */}
        {msg.pending_action && !msg.executed && (
          <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
            {/* Data editável para add_prazo */}
            {msg.action === "add_prazo" && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Data do prazo (confirme ou ajuste):</p>
                <input
                  type="date"
                  value={dateOverride}
                  onChange={e => setDateOverride(e.target.value)}
                  data-testid="ai-prazo-date-input"
                  className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#0b679e]"
                />
              </div>
            )}
            {msg.processo_choices?.length > 1 && (
              <>
                <p className="text-xs font-medium text-slate-500 mb-1">Selecione o processo:</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {msg.processo_choices.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedId(p.id)}
                      data-testid={`ai-processo-choice-${p.id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs border transition-all ${
                        selectedId === p.id
                          ? "bg-[#0b679e] text-white border-[#0b679e]"
                          : "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
                      }`}
                    >
                      <span className="font-medium block truncate">{p.nome_cliente}</span>
                      <span className={`text-[10px] ${selectedId === p.id ? "text-blue-200" : "text-slate-400"}`}>
                        {p.numero_processo} · {p.situacao}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {msg.processo_choices?.length === 1 && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-xs">
                <p className="font-semibold text-[#0b679e] truncate">{msg.processo_choices[0].nome_cliente}</p>
                <p className="text-slate-500 mt-0.5">{msg.processo_choices[0].numero_processo} · {msg.processo_choices[0].situacao}</p>
              </div>
            )}
            <button
              onClick={() => {
                const proc = msg.processo_choices?.find(p => p.id === selectedId);
                const finalParams = msg.action === "add_prazo"
                  ? { ...msg.params, data: dateOverride }
                  : msg.params;
                if (proc) onExecute(msg, proc, finalParams);
              }}
              disabled={!selectedId || (msg.action === "add_prazo" && !dateOverride)}
              data-testid="ai-confirm-action-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs px-4 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center gap-1.5"
            >
              <CheckCircle size={13} /> Confirmar e executar
            </button>
          </div>
        )}

        {/* Ação executada */}
        {msg.executed && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
            <CheckCircle size={13} /> Concluído
          </div>
        )}

        <p className="text-[10px] text-slate-400 mt-1.5">{msg.ts}</p>
      </div>
    </div>
  );
}

// ── ChatMessages ───────────────────────────────────────────────────────────────
function ChatMessages({ messages, loading, onExecute, onEasterEgg, bottomRef }) {
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
      {messages.map(msg => (
        <MessageBubble key={msg.id} msg={msg} onExecute={onExecute} onEasterEgg={onEasterEgg} />
      ))}
      {loading && (
        <div className="flex justify-start gap-2">
          <BotAvatar size="sm" />
          <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
            <div className="flex gap-1 items-center">
              {[0, 150, 300].map(d => (
                <span key={d} className="w-2 h-2 bg-[#0b679e] rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
              ))}
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

// ── ChatInputArea ─────────────────────────────────────────────────────────────
function ChatInputArea({ input, setInput, handleSend, loading, uploadingFile, fileRef, inputRef, pendingFile, setPendingFile, handleFileSelect }) {
  return (
    <>
      {(pendingFile || uploadingFile) && (
        <div className="px-3 py-2 bg-blue-50 border-t border-blue-100 flex items-center gap-2 text-xs text-[#0b679e] flex-shrink-0">
          {uploadingFile ? (
            <><Loader2 size={12} className="animate-spin" /> A carregar ficheiro...</>
          ) : (
            <>
              <Paperclip size={12} />
              <span className="truncate flex-1">{pendingFile.displayName}</span>
              <button onClick={() => setPendingFile(null)} className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"><X size={12} /></button>
            </>
          )}
        </div>
      )}
      <div className="p-3 border-t border-slate-200 bg-white flex-shrink-0">
        <div className="flex items-end gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={uploadingFile}
            className="text-slate-400 hover:text-[#0b679e] disabled:opacity-40 transition-colors flex-shrink-0 pb-1.5"
            title="Anexar ficheiro" data-testid="ai-attach-btn"><Paperclip size={18} /></button>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Escreva um comando... (Enter para enviar)" rows={2} data-testid="ai-chat-input"
            className="flex-1 resize-none text-sm border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0b679e] text-slate-900 placeholder-slate-400" />
          <button onClick={handleSend} disabled={loading || !input.trim()} data-testid="ai-chat-send-btn"
            className="flex-shrink-0 w-9 h-9 bg-[#0b679e] hover:bg-[#084e78] disabled:opacity-40 text-white rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95">
            <Send size={15} />
          </button>
        </div>
        <input ref={fileRef} type="file" className="hidden" data-testid="ai-file-input" onChange={handleFileSelect} />
      </div>
    </>
  );
}

// ── AIChatWidget ───────────────────────────────────────────────────────────────
export default function AIChatWidget() {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const fileRef    = useRef(null);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const sessionId  = useRef(getOrCreateSession());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  function now() {
    return new Date().toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  }
  function makeId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  function addMsg(role, content, extra = {}) {
    setMessages(prev => [...prev, { id: makeId(), role, content, ts: now(), ...extra }]);
  }

  useEffect(() => {
    if (open && messages.length === 0) {
      const welcomeId = makeId();
      const eggId = makeId();
      setMessages([
        {
          id: welcomeId,
          role: "assistant",
          content: "Olá! Sou o Rui Pires BOT, o seu assistente de finanças e recursos humanos no portal ACD Advogados.\n\nPosso ajudá-lo a:\n• Consultar honorários e despesas registados\n• Arquivar documentos num processo\n• Consultar informações de um processo\n• Criar tarefas num processo\n• Adicionar prazos ao calendário judicial\n• Consultar registos de ponto\n\nSe anexar uma notificação, posso sugerir a data do prazo automaticamente.",
          ts: now(),
        }
      ]);
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFile(true);
    try {
      const result = await chunkedUpload(file);
      setPendingFile({ displayName: file.name, url: result.file_url, serverName: result.file_name });
    } catch {
      addMsg("system", "Erro ao carregar ficheiro. Tente novamente.");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const file = pendingFile;
    const displayText = text + (file ? `  📎 ${file.displayName}` : "");
    addMsg("user", displayText);
    setInput("");
    setPendingFile(null);

    // Easter egg trigger (local, sem chamada à API)
    const easterTriggers = ["mini logo", "minilogo", "mini intro", "easter egg"];
    if (easterTriggers.some(t => text.toLowerCase().includes(t))) {
      setShowEasterEgg(true);
      addMsg("assistant", "Aqui vai a mini logo de início! 🎬✨ Espero que gostes.");
      return;
    }

    setLoading(true);
    try {
      const history = messages
        .filter(m => m.role === "user" || m.role === "assistant")
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const { data } = await axios.post(
        `${API}/ai/chat`,
        {
          session_id: sessionId.current,
          message: text,
          history,
          file_url: file?.url || null,
          file_name: file?.serverName || null,
        },
        { headers: getAuthHeader() }
      );

      if (data.type === "action_confirm") {
        addMsg("assistant", data.message, {
          pending_action: true,
          action: data.action,
          params: data.params,
          processo_choices: data.processo_choices,
          file_url: file?.url || null,
          file_name: file?.serverName || null,
        });
      } else {
        addMsg("assistant", data.message || "Não entendi. Pode reformular?");
      }
    } catch {
      addMsg("assistant", "Ocorreu um erro ao contactar o assistente. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (msg, processo, paramsOverride) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/execute`,
        {
          action: msg.action,
          processo_id: processo.id,
          params: paramsOverride || msg.params,
          file_url: msg.file_url,
          file_name: msg.file_name,
        },
        { headers: getAuthHeader() }
      );
      if (data.success) {
        setMessages(prev => prev.map(m => m === msg ? { ...m, executed: true } : m));
        addMsg("assistant", `✓ ${data.message}\n\nProcesso: ${processo.nome_cliente} (${processo.numero_processo})`);
      }
    } catch {
      addMsg("assistant", "Erro ao executar a ação. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleEasterEgg = useCallback(() => setShowEasterEgg(true), []);

  const applySuggestion = (s) => {
    setInput(s.replace(/\[cliente\]/g, "").replace(/\[data\]/g, ""));
    inputRef.current?.focus();
  };

  return (
    <>
      {/* Floating button — foto do Rui Pires */}
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="ai-chat-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-xl overflow-hidden transition-all duration-200 hover:scale-105 active:scale-95 border-2 border-white"
        style={{ boxShadow: "0 4px 20px rgba(11,103,158,0.5)" }}
        title="Rui Pires BOT"
      >
        {open ? (
          <div className="w-full h-full bg-[#0b679e] flex items-center justify-center">
            <X size={22} className="text-white" />
          </div>
        ) : (
          <img src={BOT_PHOTO} alt="Rui Pires BOT" className="w-full h-full object-cover" />
        )}
        {!open && messages.length === 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ height: "540px" }}
        >
          {/* Easter egg overlay */}
          {showEasterEgg && (
            <EasterEggOverlay onClose={() => setShowEasterEgg(false)} />
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-[#0b679e] to-[#084e78] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <BotAvatar size="lg" />
            <div>
              <p className="font-bold text-sm">Rui Pires BOT</p>
              <p className="text-xs text-blue-200">Finanças & RH • ACD Advogados</p>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto text-white/60 hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>
          {/* Messages */}
          <ChatMessages
            messages={messages} loading={loading}
            onExecute={handleExecute} onEasterEgg={handleEasterEgg}
            bottomRef={bottomRef}
          />

          {/* Suggestions */}
          {messages.filter(m => m.role === "user").length === 0 && (
            <div className="px-3 py-2 border-t border-slate-100 bg-white flex flex-col gap-1 flex-shrink-0">
              <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Sugestões</p>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => applySuggestion(s)}
                  className="text-left text-xs text-[#0b679e] hover:bg-blue-50 px-2 py-1 rounded transition-colors truncate">{s}</button>
              ))}
            </div>
          )}

          {/* Input + File Indicator */}
          <ChatInputArea
            input={input} setInput={setInput} handleSend={handleSend}
            loading={loading} uploadingFile={uploadingFile}
            fileRef={fileRef} inputRef={inputRef}
            pendingFile={pendingFile} setPendingFile={setPendingFile}
            handleFileSelect={handleFileSelect}
          />
        </div>
      )}
    </>
  );
}
